/**
 * Le parole dei vocali di MUNE (fetta 2G, 29/10/2026; os-planning/ai/fase-2g-vocali.md, «Parole a
 * schermo») e le funzioni pure che le scelgono. Importa solo @masamune/shared: lo prova
 * apps/web/test/vocali-testo.test.ts senza browser.
 *
 * Le frasi col nome di un file allegato le compone il web: quelle del server non hanno mai il nome
 * (il gestore degli errori le scrive nel log).
 */
import { LIMITI_VOCALI } from '@masamune/shared';

export const PAROLE_VOCALI = {
  registra: 'Registra un vocale',
  ferma: 'Ferma e trascrivi',
  annulla: 'Annulla il vocale',
  registro: 'Registro',
  apro: 'Apro il microfono…',
  trascrivo: 'Trascrivo il vocale…',
  trascrivoAltri: (n: number) => `Trascrivo ${n} vocali…`,
  microfonoNegato: 'Il microfono non è consentito: abilitalo per questo sito dal lucchetto accanto all\'indirizzo, poi riprova.',
  microfonoAssente: 'Non trovo un microfono: collegane uno e riprova.',
  microfonoOccupato: 'Il microfono è usato da un\'altra app: chiudila e riprova.',
  registrazioneNonVa: 'Questo browser non registra i vocali: allega un file audio con la graffetta.',
  troppiInCoda: `Al massimo ${LIMITI_VOCALI.perVolta} vocali per volta: aspetta che finiscano di trascriversi.`,
  quasiFinito: 'Il vocale si ferma da solo a 5 minuti.',
  /** La frase della chat vuota coi vocali accesi: quella della 2F, più il vocale. */
  vuoto: 'Chiedimi di clienti, progetti, task, trattative e soldi: guardo nel gestionale e ti rispondo coi dati. Puoi anche incollarmi un testo da riscrivere o dividere, o mandarmi una foto, un PDF o un vocale.',
} as const;

/**
 * Le estensioni dei vocali per la graffetta. L'iPhone tratta `audio/*` come video e non fa scegliere
 * i file audio (bug WebKit 242110): servono le estensioni scritte una per una.
 */
export const ESTENSIONI_AUDIO = ['.ogg', '.opus', '.oga', '.m4a', '.mp3', '.wav', '.webm', '.flac'] as const;
export const ACCETTA_AUDIO = ESTENSIONI_AUDIO.join(',');

/**
 * Un file è un vocale: dal tipo che dice il browser o, quando il tipo manca o è generico (un .opus
 * salvato da WhatsApp spesso arriva senza tipo, un .webm come video), dall'estensione.
 */
export function eAudio(f: { name: string; type: string }): boolean {
  if (f.type.startsWith('audio/')) return true;
  const nome = f.name.toLowerCase();
  if (!ESTENSIONI_AUDIO.some((e) => nome.endsWith(e))) return false;
  return !f.type || ['video/webm', 'video/ogg', 'application/ogg', 'application/octet-stream'].includes(f.type);
}

/** «0:07», «4:32». */
export function tempo(secondi: number): string {
  const s = Math.max(0, Math.floor(secondi));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Il tipo che registra il browser, nell'ordine in cui lo vogliamo, e l'estensione da dare al file. */
export const TIPI_REGISTRAZIONE = [
  { tipo: 'audio/webm;codecs=opus', ext: 'webm' },
  { tipo: 'audio/webm', ext: 'webm' },
  { tipo: 'audio/mp4', ext: 'm4a' },
  { tipo: 'audio/ogg;codecs=opus', ext: 'ogg' },
] as const;

/** «tra un minuto», «tra 3 minuti», «domani» dai secondi di attesa del servizio. */
function quando(secondi: number | null | undefined): string {
  if (!secondi || secondi <= 60) return 'tra un minuto';
  if (secondi > 3600) return 'domani';
  return `tra ${Math.ceil(secondi / 60)} minuti`;
}

/**
 * La frase di un errore di POST /ai/vocali. `nome`: il file allegato, o null per un vocale registrato
 * dal pannello. Per i codici che non conosce vale il `message` del server.
 */
export function fraseErroreVocale(codice: string, details: unknown, nome: string | null, messaggio: string): string {
  const d = (details ?? {}) as { riprovaTra?: number | null };
  const il = nome ? `"${nome}"` : 'Il vocale';
  switch (codice) {
    case 'VOCALE_PESO':
      return `${il} pesa più di ${LIMITI_VOCALI.mb} MB: MUNE trascrive vocali fino a ${LIMITI_VOCALI.mb} MB.`;
    case 'VOCALE_TIPO':
      return nome
        ? `"${nome}" non è un vocale che MUNE sa trascrivere: vanno bene i vocali di WhatsApp e i file M4A, MP3, WAV, WebM e FLAC.`
        : messaggio;
    case 'VOCALE_CORTO':
      return `${il} è troppo corto: non c'è niente da trascrivere.`;
    case 'VOCALE_LUNGO':
      return `${il} dura più di ${LIMITI_VOCALI.secondi / 60} minuti: MUNE trascrive vocali fino a ${LIMITI_VOCALI.secondi / 60} minuti.`;
    case 'VOCALE_ROVINATO':
      return nome ? `"${nome}" non si apre: forse è rovinato.` : 'Il vocale non si apre: riprova a registrarlo.';
    case 'VOCALE_VUOTO':
      return nome ? `In "${nome}" non ho sentito parole.` : 'Nel vocale non ho sentito parole: riprova più vicino al microfono.';
    case 'VOCALE_OCCUPATO':
      return `Il servizio che trascrive i vocali è occupato: riprova ${quando(d.riprovaTra)}.`;
    default:
      return messaggio;
  }
}
